'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle,
  Shield, FileText, BarChart3, Users, Copy, Check,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { sendPasswordResetEmail } from '@/lib/emailService';


interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

type DemoRole = 'Vendedor' | 'Supervisor' | 'Compras' | 'Administrador';

interface DemoCredential {
  id: string;
  role: DemoRole;
  email: string;
  password: string;
  description: string;
}

const demoCredentials: DemoCredential[] = [
  { id: 'demo-admin', role: 'Administrador', email: 'marlon@barolit.com', password: 'Barolit@2026!', description: 'Acceso total al sistema' },
];

const roleColors: Record<DemoRole, string> = {
  Vendedor: 'bg-blue-100 text-blue-700',
  Supervisor: 'bg-purple-100 text-purple-700',
  Compras: 'bg-amber-100 text-amber-700',
  Administrador: 'bg-green-100 text-green-700',
};

const features = [
  { id: 'feat-quotes', icon: FileText, text: 'Cotizaciones con historial de versiones' },
  { id: 'feat-approval', icon: CheckCircle, text: 'Flujo de aprobación por supervisor' },
  { id: 'feat-stats', icon: BarChart3, text: 'Estadísticas de pipeline y cierre' },
  { id: 'feat-roles', icon: Users, text: 'Multi-usuario con roles y permisos' },
];

export default function LoginScreen() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMsLoading, setIsMsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [loginError, setLoginError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const { signIn } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const handleCopy = async (text: string, fieldId: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(fieldId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const autofill = (cred: DemoCredential) => {
    setValue('email', cred.email, { shouldValidate: true });
    setValue('password', cred.password, { shouldValidate: true });
    setLoginError('');
    toast.info(`Credenciales de ${cred.role} cargadas`, { description: cred.email });
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError('');
    try {
      await signIn(data.email, data.password);
      toast.success('Bienvenido a QuoteDesk', { description: 'Sesión iniciada correctamente' });
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setLoginError(err?.message === 'Invalid login credentials' ?'Credenciales inválidas — verifica tu email y contraseña' : err?.message ||'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsMsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    toast.info('Autenticación con Microsoft', { description: 'Redirigiendo a Microsoft EntraID...' });
    setIsMsLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotMessage('error:Ingresa un correo electrónico válido.');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const supabase = createClient();
      const redirectTo = `${window.location.origin}/auth/callback?type=recovery`;
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo,
      });
      if (error) throw error;

      // Also send branded password reset email via Resend
      try {
        await sendPasswordResetEmail({
          userEmail: forgotEmail,
          resetLink: redirectTo,
        });
      } catch {
        // Resend email is supplemental; Supabase already sent the reset link
      }

      setForgotMessage('success:Se ha enviado un enlace de recuperación a ' + forgotEmail + '. Revisa tu bandeja de entrada.');
    } catch (err: any) {
      setForgotMessage('error:' + (err?.message || 'Error al enviar el correo de recuperación.'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] flex-col justify-between p-10 xl:p-14 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute top-1/3 -left-16 w-64 h-64 rounded-full bg-white/5" />
          <div className="absolute bottom-24 right-12 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={44} />
            <div>
              <span className="font-extrabold text-2xl text-white tracking-tight">QuoteDesk</span>
              <p className="text-blue-200 text-xs font-medium">by Baroli Technologies</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <h1 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
            Cotizaciones profesionales,<br />
            <span className="text-blue-200">sin el caos de Excel.</span>
          </h1>
          <p className="text-blue-100 text-base leading-relaxed mb-8 max-w-sm">
            Crea, aprueba y da seguimiento a cotizaciones con flujo estructurado, historial de versiones y estadísticas en tiempo real.
          </p>
          <div className="space-y-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-white" />
                  </div>
                  <span className="text-blue-100 text-sm font-medium">{f.text}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm">
            <Shield size={16} className="text-blue-200 shrink-0" />
            <p className="text-blue-100 text-xs">
              Autenticación segura con <strong className="text-white">Supabase Auth</strong> — sesiones persistentes y control de roles
            </p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-14 xl:px-20 py-10 overflow-y-auto scrollbar-thin">
        <div className="w-full max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-extrabold text-xl text-foreground tracking-tight">QuoteDesk</span>
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">Iniciar Sesión</h2>
            <p className="text-muted-foreground text-sm mt-1.5">
              Accede con tu cuenta de Microsoft 365 o con email y contraseña.
            </p>
          </div>

          <button
            type="button"
            onClick={handleMicrosoftLogin}
            disabled={isMsLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-border bg-card hover:border-primary/30 hover:bg-primary-light/20 transition-all duration-150 active:scale-[0.99] disabled:opacity-60 mb-6"
          >
            {isMsLoading ? (
              <span className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <MicrosoftLogo />
            )}
            <span className="text-sm font-semibold text-foreground">
              {isMsLoading ? 'Redirigiendo...' : 'Continuar con Microsoft'}
            </span>
          </button>

          <div className="relative flex items-center mb-6">
            <div className="flex-1 border-t border-border" />
            <span className="px-3 text-xs text-muted-foreground font-medium bg-background">o con email y contraseña</span>
            <div className="flex-1 border-t border-border" />
          </div>

          {loginError && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-danger-light border border-red-200 mb-5 fade-in">
              <AlertCircle size={15} className="text-danger shrink-0 mt-0.5" />
              <p className="text-sm text-danger font-medium leading-relaxed">{loginError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label htmlFor="email" className="label-base">Correo Electrónico</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="usuario@techsolrd.com"
                  {...register('email', {
                    required: 'El correo es requerido',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ingresa un correo válido' },
                  })}
                  className={`input-base pl-10 ${errors.email ? 'border-danger focus:ring-danger' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-danger mt-1.5 flex items-center gap-1">
                  <AlertCircle size={11} /> {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="label-base mb-0">Contraseña</label>
                <button
                  type="button"
                  onClick={() => { setShowForgotModal(true); setForgotMessage(''); setForgotEmail(''); }}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  {...register('password', {
                    required: 'La contraseña es requerida',
                    minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                  })}
                  className={`input-base pl-10 pr-10 ${errors.password ? 'border-danger focus:ring-danger' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-danger mt-1.5 flex items-center gap-1">
                  <AlertCircle size={11} /> {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                {...register('remember')}
                className="w-4 h-4 rounded border-border accent-primary"
              />
              <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer select-none">
                Mantener sesión iniciada
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 justify-center text-base font-bold mt-2 transition-all"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Iniciando sesión...
                </span>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Cuentas Demo</span>
              <div className="flex-1 border-t border-border" />
            </div>
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Rol</th>
                    <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Email</th>
                    <th className="px-3 py-2 text-center font-semibold text-muted-foreground">Usar</th>
                  </tr>
                </thead>
                <tbody>
                  {demoCredentials.map((cred) => (
                    <tr key={cred.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${roleColors[cred.role]}`}>
                          {cred.role}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-foreground font-medium truncate max-w-[130px]">{cred.email}</span>
                          <button
                            type="button"
                            onClick={() => handleCopy(cred.email, `${cred.id}-email`)}
                            className="p-0.5 text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title="Copiar email"
                          >
                            {copiedId === `${cred.id}-email` ? <Check size={10} className="text-success" /> : <Copy size={10} />}
                          </button>
                        </div>
                        <p className="text-muted-foreground text-[10px] mt-0.5">{cred.description}</p>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => autofill(cred)}
                          className="px-2.5 py-1 rounded-lg bg-primary-light text-primary text-[11px] font-semibold hover:bg-primary hover:text-white transition-all duration-150 active:scale-95"
                        >
                          Usar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 text-center">
              Haz clic en "Usar" para autocompletar las credenciales del rol deseado
            </p>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-8">
            Al iniciar sesión aceptas los{' '}
            <a href="#" className="text-primary hover:underline font-medium">Términos de Servicio</a>
            {' '}y la{' '}
            <a href="#" className="text-primary hover:underline font-medium">Política de Privacidad</a>
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-card rounded-2xl shadow-xl border border-border w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">Recuperar Contraseña</h3>
              <button
                type="button"
                onClick={() => setShowForgotModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
                aria-label="Cerrar"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
            </p>
            <div className="mb-4">
              <label htmlFor="forgot-email" className="label-base">Correo Electrónico</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                  placeholder="usuario@ejemplo.com"
                  className="input-base pl-10"
                  autoComplete="email"
                />
              </div>
            </div>
            {forgotMessage && (
              <div className={`flex items-start gap-2 p-3 rounded-xl mb-4 text-sm ${
                forgotMessage.startsWith('success:')
                  ? 'bg-green-50 border border-green-200 text-green-700' :'bg-danger-light border border-red-200 text-danger'
              }`}>
                {forgotMessage.startsWith('success:') ? (
                  <CheckCircle size={15} className="shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                )}
                <span>{forgotMessage.replace(/^(success|error):/, '')}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForgotModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={forgotLoading || forgotMessage.startsWith('success:')}
                className="flex-1 btn-primary py-2.5 justify-center text-sm font-bold disabled:opacity-60"
              >
                {forgotLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  'Enviar Enlace'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}